import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Firestore, doc, getDoc } from 'firebase/firestore';
import { useUnitData } from '../hooks/useUnitData';
import { useUnitMembers } from '../hooks/useUnitMembers';
import { useUnitInvitations } from '../hooks/useUnitInvitations';
import { useUnitMembershipRequests } from '../hooks/useUnitMembershipRequests';
import { useUnitPermissions } from '../hooks/useUnitPermissions';
import { GeneralTab } from './tabs/GeneralTab';
import { MembersTab } from './tabs/MembersTab';
import { InvitationsTab } from './tabs/InvitationsTab';
import { GuestListTab } from './tabs/GuestListTab';
import { BrandingTab } from './tabs/BrandingTab';
import { PaymentMethodsTab } from './tabs/PaymentMethodsTab';
import { Unit } from '../types';
import {
  BarChart3,
  Users,
  Mail,
  Settings,
  Building,
  Building2,
  Wallet,
} from 'lucide-react';

type TabType = 'general' | 'members' | 'invitations' | 'guestlist' | 'branding' | 'payment' | 'venues';

interface UnitSettingsPageProps {
  unitId: string | undefined;
  currentUser: any;
  userProfile: any;
  db: Firestore;
  storage: any;
  functions: any; // Firebase Functions instance
  refreshUnits: () => Promise<void>;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  confirmAction: (options: any) => Promise<any>;
  formatDate: (date: Date) => string;
  cn: (...classes: any[]) => string;
  Header: any;
  Button: any;
  Input: any;
  Label: any;
  Textarea: any;
  Select: any;
  SelectTrigger: any;
  SelectContent: any;
  SelectItem: any;
  SelectValue: any;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Badge: any;
  Switch: any;
  Dialog?: any;
  DialogContent?: any;
  DialogHeader?: any;
  DialogTitle?: any;
  DialogDescription?: any;
  DialogFooter?: any;
  VenuesTab?: any;
}

export function UnitSettingsPage({
  unitId,
  currentUser,
  userProfile,
  db,
  storage,
  functions,
  refreshUnits,
  showSuccess,
  showError,
  confirmAction,
  formatDate,
  cn,
  Header,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  VenuesTab,
}: UnitSettingsPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('general');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['general', 'members', 'invitations', 'guestlist', 'branding', 'payment', 'venues'].includes(tab)) {
      setActiveTab(tab as TabType);
    }
  }, [searchParams]);

  // Hooks
  const { unit, setUnit, loading, guestList, setGuestList } = useUnitData(
    db,
    unitId,
    showError
  );
  const { canEdit } = useUnitPermissions(userProfile, unitId);
  const {
    members,
    loadingMembers,
    handleChangeMemberRole,
    handleRemoveMember,
    handleImportMembers: originalHandleImportMembers,
  } = useUnitMembers(db, functions, unit, activeTab, showSuccess, showError);

  // Refresh unit after a background import completes
  const refreshUnitAfterImport = async (summary?: {
    imported: number;
    skipped: number;
    failed: Array<{ email: string; reason: string }>;
  }) => {
    if (summary && summary.imported > 0) {
      showSuccess(
        `Successfully imported ${summary.imported} member${summary.imported !== 1 ? 's' : ''}`
      );
    }

    if (!unitId) return;

    try {
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists()) {
        const data = unitDoc.data();
        const unitData: Unit = {
          id: unitDoc.id,
          name: data.name,
          unitNumber: data.unitNumber,
          members: data.members || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          subscription: {
            status: data.subscription?.status || 'trial',
            trialEndsAt: data.subscription?.trialEndsAt?.toDate()
              ? data.subscription.trialEndsAt.toDate()
              : data.subscription?.trialEndsAt
              ? new Date(data.subscription.trialEndsAt)
              : undefined,
            billingEmail: data.subscription?.billingEmail || '',
          },
          settings: data.settings || {},
          billingConfig: data.billingConfig,
          contactEmail: data.contactEmail,
          contactPhone: data.contactPhone,
          address: data.address,
          description: data.description,
          guestList: data.guestList
            ? data.guestList.map((guest: any) => ({
                ...guest,
                addedAt: guest.addedAt?.toDate
                  ? guest.addedAt.toDate()
                  : new Date(guest.addedAt),
              }))
            : [],
        };
        setUnit(unitData);
      }
    } catch (error) {
      console.error('Error refreshing unit after import:', error);
    }
  };
  const { invitations, sendingInvite, handleSendInvitation, handleRevokeInvitation } =
    useUnitInvitations(
      db,
      functions,
      unitId,
      unit,
      currentUser,
      userProfile,
      members,
      activeTab,
      showSuccess,
      showError
    );
  const {
    membershipRequests,
    processingRequestId,
    handleApproveRequest,
    handleRejectRequest,
  } = useUnitMembershipRequests(
    db,
    functions,
    unitId,
    unit,
    currentUser,
    activeTab,
    canEdit,
    showSuccess,
    showError
  );

  const onApproveMembershipRequest = async (request: Parameters<typeof handleApproveRequest>[0]) => {
    const approved = await handleApproveRequest(request);
    if (approved && unitId) {
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists() && unit) {
        setUnit({ ...unit, members: unitDoc.data().members || [] });
      }
      await refreshUnits();
    }
    return approved;
  };

  // Update members in unit when they change
  const onChangeMemberRole = async (uid: string, newRole: any) => {
    const updatedMembers = await handleChangeMemberRole(uid, newRole);
    if (updatedMembers && unit) {
      setUnit({ ...unit, members: updatedMembers });
    }
  };

  const onRemoveMember = async (uid: string, memberName: string) => {
    const result = await confirmAction({
      title: 'Remove Member Access?',
      text: `${memberName} will immediately lose all access to this unit and will no longer be able to view events, RSVPs, or any unit information. This action cannot be undone.`,
      confirmButtonText: 'Remove Access',
    });

    if (result.isConfirmed) {
      const updatedMembers = await handleRemoveMember(uid, memberName);
      if (updatedMembers && unit) {
        setUnit({ ...unit, members: updatedMembers });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Unit Not Found</CardTitle>
              <CardDescription>The unit you're looking for doesn't exist.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                ← Back to Dashboard
              </Button>
              {canEdit && unit.creditBalance !== undefined && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/unit/${unit.id}/credits`)}
                >
                  <Wallet className="mr-2 h-4 w-4" />
                  <span className={unit.creditBalance < 0 ? 'text-destructive font-semibold' : ''}>
                    {unit.creditBalance} Credits
                  </span>
                </Button>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">{unit.name}</h1>
                <p className="text-muted-foreground">Unit #{unit.unitNumber}</p>
              </div>
              <Button onClick={() => navigate(`/unit/${unitId}/analytics`)}>
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('general')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'general'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Building className="h-4 w-4" />
                General
              </button>
              <button
                onClick={() => setActiveTab('members')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'members'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Users className="h-4 w-4" />
                Members ({unit.members.length})
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'invitations'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Mail className="h-4 w-4" />
                Invitations
                {invitations.length > 0 && (
                  <Badge variant="default" className="ml-1">
                    {invitations.length}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => setActiveTab('guestlist')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'guestlist'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Mail className="h-4 w-4" />
                Guest List
                {guestList.length > 0 && (
                  <Badge variant="default" className="ml-1">
                    {guestList.length}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => setActiveTab('payment')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'payment'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Wallet className="h-4 w-4" />
                Payment Methods
              </button>
              <button
                onClick={() => setActiveTab('branding')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                  activeTab === 'branding'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Settings className="h-4 w-4" />
                Branding
              </button>
              {VenuesTab && (
                <button
                  onClick={() => setActiveTab('venues')}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 border-b-2 font-medium transition-colors',
                    activeTab === 'venues'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Building2 className="h-4 w-4" />
                  Venues
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'general' && (
            <GeneralTab
              unit={unit}
              setUnit={setUnit}
              canEdit={canEdit}
              db={db}
              showSuccess={showSuccess}
              showError={showError}
              refreshUnits={refreshUnits}
              formatDate={formatDate}
              Button={Button}
              Input={Input}
              Label={Label}
              Textarea={Textarea}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
            />
          )}

          {activeTab === 'members' && (
            <MembersTab
              members={members}
              loadingMembers={loadingMembers}
              canEdit={canEdit}
              currentUser={currentUser}
              handleChangeMemberRole={onChangeMemberRole}
              handleRemoveMember={onRemoveMember}
              handleImportMembers={originalHandleImportMembers}
              unitId={unitId}
              db={db}
              onImportComplete={refreshUnitAfterImport}
              formatDate={formatDate}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
              Button={Button}
              Badge={Badge}
              Select={Select}
              SelectTrigger={SelectTrigger}
              SelectContent={SelectContent}
              SelectItem={SelectItem}
              SelectValue={SelectValue}
              Dialog={Dialog}
              DialogContent={DialogContent}
              DialogHeader={DialogHeader}
              DialogTitle={DialogTitle}
              DialogDescription={DialogDescription}
              DialogFooter={DialogFooter}
              Label={Label}
              Input={Input}
            />
          )}

          {activeTab === 'invitations' && (
            <InvitationsTab
              canEdit={canEdit}
              invitations={invitations}
              membershipRequests={membershipRequests}
              processingRequestId={processingRequestId}
              sendingInvite={sendingInvite}
              handleSendInvitation={handleSendInvitation}
              handleRevokeInvitation={handleRevokeInvitation}
              handleApproveRequest={onApproveMembershipRequest}
              handleRejectRequest={handleRejectRequest}
              confirmAction={confirmAction}
              formatDate={formatDate}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
              Button={Button}
              Input={Input}
              Label={Label}
              Select={Select}
              SelectTrigger={SelectTrigger}
              SelectContent={SelectContent}
              SelectItem={SelectItem}
              SelectValue={SelectValue}
              Badge={Badge}
            />
          )}

          {activeTab === 'guestlist' && (
            <GuestListTab
              unitId={unit.id}
              guestList={guestList}
              setGuestList={setGuestList}
              canEdit={canEdit}
              currentUser={currentUser}
              db={db}
              confirmAction={confirmAction}
              formatDate={formatDate}
              showSuccess={showSuccess}
              showError={showError}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
              Button={Button}
              Input={Input}
              Label={Label}
              Badge={Badge}
              Select={Select}
              SelectTrigger={SelectTrigger}
              SelectContent={SelectContent}
              SelectItem={SelectItem}
              SelectValue={SelectValue}
              Dialog={Dialog}
              DialogContent={DialogContent}
              DialogHeader={DialogHeader}
              DialogTitle={DialogTitle}
              DialogDescription={DialogDescription}
              DialogFooter={DialogFooter}
            />
          )}

          {activeTab === 'payment' && (
            <PaymentMethodsTab
              unit={unit}
              db={db}
              functions={functions}
              showSuccess={showSuccess}
              showError={showError}
              Button={Button}
              Input={Input}
              Label={Label}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
              Badge={Badge}
              Switch={Switch}
              Select={Select}
              SelectTrigger={SelectTrigger}
              SelectContent={SelectContent}
              SelectItem={SelectItem}
              SelectValue={SelectValue}
              onUpdate={refreshUnits}
            />
          )}

          {activeTab === 'branding' && (
            <BrandingTab
              unit={unit}
              setUnit={setUnit}
              canEdit={canEdit}
              db={db}
              storage={storage}
              confirmAction={confirmAction}
              showSuccess={showSuccess}
              showError={showError}
              refreshUnits={refreshUnits}
              Card={Card}
              CardContent={CardContent}
              CardDescription={CardDescription}
              CardHeader={CardHeader}
              CardTitle={CardTitle}
              Button={Button}
              Input={Input}
              Label={Label}
            />
          )}

          {activeTab === 'venues' && VenuesTab && unit && <VenuesTab unitId={unit.id} />}
        </div>
      </main>
    </div>
  );
}

